import { useState, useEffect } from 'react';
import { useMedplum } from '@medplum/react';
import { useDebouncedValue } from '@mantine/hooks';
import {
  ServiceRequest,
  Patient,
  Bundle,
  DocumentReference,
  RelatedPerson,
} from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ConsentData } from '@/types/consent.types';
import {
  createConsentResource,
  getRelationshipDisplay,
} from '@/utils/consent.utils';
import {
  CONSENT_DOCUMENT_CODING,
  CONSENT_STATUS_TAG_CODES,
} from '@consts/consent.consts';
import { EXTERNAL_CS, HL7, SYSTEMS } from '@/consts/systems.consts';

// Helper function to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

interface UseConsentOperationsProps {
  serviceRequest?: ServiceRequest;
  patient?: Patient;
  existingConsentId?: string;
  existingConsentMeta?: any;
  onConsentUpdate?: () => void;
  onCloseModal?: () => void;
  showModal?: boolean;
  consentData?: ConsentData;
  isReadOnly?: boolean;
}

export function useConsentOperations({
  serviceRequest,
  patient,
  existingConsentId,
  existingConsentMeta,
  onConsentUpdate,
  onCloseModal,
  showModal,
  consentData,
  isReadOnly = false,
}: UseConsentOperationsProps) {
  const medplum = useMedplum();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateRelatedPerson, setDuplicateRelatedPerson] =
    useState<RelatedPerson | null>(null);

  // Debounced values to avoid firing duplicate checks on every keystroke
  const [debouncedName] = useDebouncedValue(
    consentData?.newRelatedPersonName,
    500
  );
  const [debouncedRelationship] = useDebouncedValue(
    consentData?.newRelatedPersonRelationship,
    500
  );

  // Check for duplicate RelatedPerson when name and relationship are entered
  useEffect(() => {
    const checkForDuplicate = async () => {
      // Only check for mother (MTH) or father (FTH) relationships
      if (
        !patient?.id ||
        !debouncedName?.trim() ||
        !debouncedRelationship ||
        isReadOnly
      ) {
        return;
      }

      const relationshipCode = debouncedRelationship;
      const isMotherOrFather =
        relationshipCode === HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.MOTHER ||
        relationshipCode === HL7.TERMINOLOGY.CS.V3.ROLE_CODE.CODES.FATHER;

      if (!isMotherOrFather) {
        return;
      }

      try {
        // Search for existing RelatedPerson with same relationship
        const existingRelatedPerson = await medplum.searchOne(
          'RelatedPerson',
          {
            patient: `Patient/${patient.id}`,
            relationship: relationshipCode,
          }
        );

        if (existingRelatedPerson) {
          setDuplicateRelatedPerson(existingRelatedPerson);
          setShowDuplicateWarning(true);
        } else {
          setDuplicateRelatedPerson(null);
          setShowDuplicateWarning(false);
        }
      } catch (error) {
        console.error('Error checking for duplicate RelatedPerson:', error);
      }
    };

    checkForDuplicate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName, debouncedRelationship, patient?.id, isReadOnly]);

  const handleSaveConsent = async (
    consentData: ConsentData,
    uploadedFiles: File[],
    existingConsentFiles: string[] = []
  ) => {
    if (!serviceRequest?.id || !patient?.id) {
      notifications.show({
        title: t('error', { ns: 'common' }),
        message: t('consent.notifications.missingInfo', { ns: 'lims' }),
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const bundle: Bundle = {
        resourceType: 'Bundle',
        type: 'transaction',
        entry: [],
      };

      // Upload files and create DocumentReferences
      const documentReferenceIds: string[] = [];
      const consentDocumentIds: string[] = [...existingConsentFiles]; // Start with existing consent files

      // Handle authorization document separately if present
      if (consentData.authorizationDocument) {
        const base64Data = await fileToBase64(
          consentData.authorizationDocument
        );
        const authDocReference: DocumentReference = {
          resourceType: 'DocumentReference',
          status: 'current',
          type: {
            coding: [
              {
                system: SYSTEMS.LOINC,
                code: EXTERNAL_CS.LOINC.CODES.AUTHORIZATION_DOCUMENT,
                display: 'Authorization for release of information',
              },
            ],
          },
          subject: { reference: `Patient/${patient.id}` },
          date: new Date().toISOString(),
          description: `Authorization document for consent - ${serviceRequest.identifier?.[0]?.value || 'Order'}`,
          content: [
            {
              attachment: {
                contentType: consentData.authorizationDocument.type,
                data: base64Data.split(',')[1],
                title: consentData.authorizationDocument.name,
                size: consentData.authorizationDocument.size,
              },
            },
          ],
          context: {
            related: [
              {
                reference: `ServiceRequest/${serviceRequest.id}`,
              },
            ],
          },
        };

        bundle.entry?.push({
          resource: authDocReference,
          request: {
            method: 'POST',
            url: 'DocumentReference',
          },
        });
      }

      // Handle regular consent files
      for (const file of uploadedFiles) {
        const base64Data = await fileToBase64(file);
        const documentReference: DocumentReference = {
          resourceType: 'DocumentReference',
          status: 'current',
          type: {
            coding: [CONSENT_DOCUMENT_CODING],
          },
          subject: { reference: `Patient/${patient.id}` },
          date: new Date().toISOString(),
          description: `Consent document for ${serviceRequest.identifier?.[0]?.value || 'Order'}`,
          content: [
            {
              attachment: {
                contentType: file.type,
                data: base64Data.split(',')[1], // Remove data URL prefix
                title: file.name,
                size: file.size,
              },
            },
          ],
          context: {
            related: [
              {
                reference: `ServiceRequest/${serviceRequest.id}`,
              },
            ],
          },
        };

        // Add to bundle
        bundle.entry?.push({
          resource: documentReference,
          request: {
            method: 'POST',
            url: 'DocumentReference',
          },
        });
      }

      // Determine performer reference
      let performerReference: string;
      if (consentData.signerType === 'patient') {
        performerReference = `Patient/${patient.id}`;
      } else if (consentData.signerType === 'other') {
        if (consentData.selectedRelatedPerson?.id) {
          // Use existing RelatedPerson
          performerReference = `RelatedPerson/${consentData.selectedRelatedPerson.id}`;
        } else if (
          consentData.newRelatedPersonName &&
          consentData.newRelatedPersonRelationship
        ) {
          // Create new RelatedPerson
          const relatedPerson = {
            resourceType: 'RelatedPerson' as const,
            patient: { reference: `Patient/${patient.id}` },
            relationship: [
              {
                coding: [
                  {
                    system: HL7.TERMINOLOGY.CS.V3.ROLE_CODE.BASE,
                    code: consentData.newRelatedPersonRelationship,
                    display: getRelationshipDisplay(
                      consentData.newRelatedPersonRelationship
                    ),
                  },
                ],
              },
            ],
            name: [
              {
                text: consentData.newRelatedPersonName,
              },
            ],
          };

          // Add RelatedPerson to bundle
          bundle.entry?.push({
            resource: relatedPerson,
            request: {
              method: 'POST',
              url: 'RelatedPerson',
            },
          });

          // We'll get the ID after the bundle is executed
          performerReference = 'pending';
        } else {
          // Fallback to patient if no valid RelatedPerson data
          performerReference = `Patient/${patient.id}`;
        }
      } else {
        // Fallback to patient
        performerReference = `Patient/${patient.id}`;
      }

      // Delete existing RelatedPerson if replacement is requested
      if (consentData.existingRelatedPersonIdToDelete) {
        try {
          await medplum.deleteResource(
            'RelatedPerson',
            consentData.existingRelatedPersonIdToDelete
          );
        } catch (error) {
          console.error(
            `Error deleting existing RelatedPerson ${consentData.existingRelatedPersonIdToDelete}:`,
            error
          );
          // Continue with creation even if deletion fails
        }
      }

      // Execute bundle to create documents and RelatedPerson if needed
      let uploadedDocRefs: DocumentReference[] = [];
      let relatedPersonId: string | undefined;
      if (bundle.entry && bundle.entry.length > 0) {
        const bundleResult = await medplum.executeBatch(bundle);
        if (bundleResult.entry) {
          const uploadedResources = bundleResult.entry
            .map((e) => e.resource)
            .filter(
              (r) =>
                r?.resourceType === 'DocumentReference' ||
                r?.resourceType === 'RelatedPerson'
            );

          // Get document reference IDs and separate consent files from authorization files
          uploadedDocRefs = uploadedResources.filter(
            (r): r is DocumentReference =>
              r?.resourceType === 'DocumentReference'
          );

          // Separate consent files (for sourceReference) from authorization files
          const consentFiles = uploadedDocRefs.filter(
            (d) =>
              d.type?.coding?.[0]?.code === CONSENT_DOCUMENT_CODING.code &&
              d.type?.coding?.[0]?.system === CONSENT_DOCUMENT_CODING.system
          );

          // Add consent files to sourceReference array
          consentDocumentIds.push(
            ...consentFiles.filter((d) => d.id).map((d) => d.id as string)
          );

          // Add all files to general documentReferenceIds (for backward compatibility)
          documentReferenceIds.push(
            ...uploadedDocRefs.filter((d) => d.id).map((d) => d.id as string)
          );

          // Get RelatedPerson ID if created
          const createdRelatedPerson = uploadedResources.find(
            (r) => r?.resourceType === 'RelatedPerson'
          );
          if (createdRelatedPerson?.id) {
            relatedPersonId = createdRelatedPerson.id;
          }
        }
      }

      // Set final performer reference
      if (performerReference === 'pending' && relatedPersonId) {
        performerReference = `RelatedPerson/${relatedPersonId}`;
      } else if (performerReference === 'pending') {
        performerReference = `Patient/${patient.id}`; // Fallback to patient
      }

      // Create or update consent with document references and performer
      const consent = createConsentResource(
        consentData,
        patient.id,
        serviceRequest.id,
        serviceRequest.identifier?.[0]?.value || '',
        t,
        consentDocumentIds, // Use consent files for sourceReference
        performerReference
      );

      if (existingConsentId) {
        // Update existing consent
        await medplum.updateResource({
          ...consent,
          id: existingConsentId,
          meta: existingConsentMeta,
        });
      } else {
        // Create new consent
        await medplum.createResource(consent);
      }

      // Update ServiceRequest with Consent Status Tag
      if (serviceRequest.id && consent.provision?.type) {
        try {
          const latestSR = await medplum.readResource(
            'ServiceRequest',
            serviceRequest.id
          );

          let newTag;
          if (consent.provision.type === 'deny') {
            newTag = CONSENT_STATUS_TAG_CODES.DENY;
          } else if (consent.provision.type === 'permit') {
            newTag = CONSENT_STATUS_TAG_CODES.PERMIT;
          } else {
            newTag = CONSENT_STATUS_TAG_CODES.NOT_ANSWERED;
          }

          const currentTags = latestSR.meta?.tag || [];
          // Remove any existing consent status tags
          const otherTags = currentTags.filter(
            (t) => t.system !== CONSENT_STATUS_TAG_CODES.PERMIT.system
          );
          const updatedTags = [...otherTags, newTag];

          await medplum.updateResource({
            ...latestSR,
            meta: {
              ...latestSR.meta,
              tag: updatedTags,
            },
          });
        } catch (error) {
          console.error('Error updating ServiceRequest consent tag:', error);
          // We don't block the success notification if this fails, but it's good to log
        }
      }

      notifications.show({
        title: t('success', { ns: 'common' }),
        message: t('consent.notifications.saveSuccess', { ns: 'lims' }),
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      if (onConsentUpdate) {
        onConsentUpdate();
      }

      if (showModal && onCloseModal) {
        onCloseModal();
      }

      return true;
    } catch (error: any) {
      notifications.show({
        title: t('error', { ns: 'common' }),
        message:
          error.message || t('consent.notifications.saveError', { ns: 'lims' }),
        color: 'red',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    handleSaveConsent,
    loading,
    showDuplicateWarning,
    duplicateRelatedPerson,
    setShowDuplicateWarning,
    setDuplicateRelatedPerson,
  };
}
